import { getRulesStore, getScreenTimeStore } from '../storage/stores';
import { isDelegationStarted } from '../delegation/delegation';
import { isSafeNetworkDelegated } from '../safeNetworks/safeNetworksDelegation';
import {
  getDateInFuture,
  getDateMs,
  getSystemTimezone,
  getWeekdayValue,
  isWeekend,
  now,
  toSeconds,
} from '../lib/dateTime';
import {
  DeviceAndUserStore,
  RulesStore,
  TimeRestrictionsStore,
} from '../types/store';
import { UNLIMITED_DAILY_QUOTA } from '../constants';
import { shouldCheckNavigation } from '../helper/rules';
import { ValueOf } from '../types';

/**
 * Correct different inconsistencies in the daily quotas received from the API.
 *
 * API has a weird format. If is_weekend_quota is `true`, then the fields `sat`
 * and `sun` have a value of 120 by default. If is_weekdays_quota is `true`,
 * then the fields `mon`, `tue`, `wed`, `thr`, `fri` have a value of 120 by
 * default.
 * The flags are redundant from the perspective of the app, so we simply use
 * these flags to set the corresponding days to zero hours restriction.
 */
export const parseDailyQuotas = (
  timeRestrictions: TimeRestrictionsStore | undefined
) => {
  if (timeRestrictions === undefined) {
    return undefined;
  }
  const {
    is_weekdays_quota: isWeekdaysQuota = false,
    is_weekend_quota: isWeekendQuota = false,
  } = timeRestrictions;

  return Object.entries(timeRestrictions.quotas).reduce((acc, entry) => {
    const [day, quota] = entry as [
      keyof TimeRestrictionsStore['quotas'],
      ValueOf<TimeRestrictionsStore['quotas']>
    ];

    if (
      (isWeekend(day) && !isWeekendQuota) ||
      (!isWeekend(day) && !isWeekdaysQuota)
    ) {
      return { ...acc, [day]: UNLIMITED_DAILY_QUOTA };
    }

    return { ...acc, [day]: quota };
  }, {} as TimeRestrictionsStore['quotas']);
};

export const getConsumedQuotaSeconds = (deviceUserData: DeviceAndUserStore) =>
  isDelegationStarted() && !isSafeNetworkDelegated()
    ? toSeconds(
        getScreenTimeStore().getScreenTime()?.totalConsumedInMillis ?? 0
      )
    : deviceUserData.consumedQuotaSeconds;

/**
 * Daily quota calculations use the rules timezone and preserve the existing
 * unlimited-day sentinel. Extra time is normalized so bad payloads cannot
 * reduce the base quota or produce fractional remaining seconds.
 */
export const getEffectiveDailyQuotaMinutes = (
  timeRestrictions: TimeRestrictionsStore | undefined,
  dailyQuota: RulesStore['dailyQuota'] | undefined,
  date: number,
  timezone: string
) => {
  const dailyQuotas = parseDailyQuotas(timeRestrictions);
  if (!dailyQuotas) {
    return undefined;
  }

  const dailyQuotaMinutes = getWeekdayValue(dailyQuotas, date, timezone);
  if (dailyQuotaMinutes === UNLIMITED_DAILY_QUOTA) {
    return UNLIMITED_DAILY_QUOTA;
  }

  return dailyQuotaMinutes + normalizeExtraTimeMinutes(dailyQuota, date);
};

const normalizeExtraTimeMinutes = (
  dailyQuota: RulesStore['dailyQuota'] | undefined,
  date: number
): number => {
  const raw = dailyQuota?.extraTimeMinutes;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    return 0;
  }
  if (
    dailyQuota &&
    'extraTimeEndsAtInSeconds' in dailyQuota &&
    !isExtraTimeActive(dailyQuota.extraTimeEndsAtInSeconds, date)
  ) {
    return 0;
  }
  return Math.max(0, Math.floor(raw));
};

const isExtraTimeActive = (
  endsAtInSeconds: number | undefined,
  date: number
): boolean => {
  if (
    typeof endsAtInSeconds !== 'number' ||
    !Number.isFinite(endsAtInSeconds) ||
    endsAtInSeconds <= 0
  ) {
    return false;
  }
  return Math.floor(date / 1000) <= endsAtInSeconds;
};

export const isOverDailyQuota = (
  deviceUserData: DeviceAndUserStore,
  date: number,
  consumedQuotaSeconds: number = getConsumedQuotaSeconds(deviceUserData),
  { shouldLog = false }: { shouldLog?: boolean } = {}
) => {
  const rulesStore = getRulesStore();
  const timeRestrictions = rulesStore?.getTimeRestrictions();
  const timezone = rulesStore?.getTimezone();
  const dailyQuotas = parseDailyQuotas(timeRestrictions);

  if (
    timeRestrictions === undefined ||
    timezone === undefined ||
    dailyQuotas === undefined ||
    !shouldCheckNavigation(timeRestrictions)
  ) {
    if (shouldLog) {
      console.log(
        `[isOverDailyQuota] false - No restrictions or navigation check disabled: timeRestrictionsUndefined=${
          timeRestrictions === undefined
        }, timezoneUndefined=${timezone === undefined}, dailyQuotasUndefined=${
          dailyQuotas === undefined
        }, shouldCheckNavigation=${
          timeRestrictions ? shouldCheckNavigation(timeRestrictions) : false
        }, result=false`
      );
    }
    return false;
  }

  const dailyQuota = getEffectiveDailyQuotaMinutes(
    timeRestrictions,
    rulesStore?.getDailyQuota?.(),
    date,
    timezone
  );

  if (dailyQuota === undefined) {
    if (shouldLog) {
      console.log('[isOverDailyQuota] false - Daily quota is undefined');
    }
    return false;
  }

  if (shouldLog) {
    console.log(
      `[isOverDailyQuota] Daily quota calculation: date=${date}, rulesTimezone=${timezone}, systemTimezone=${getSystemTimezone()}, dailyQuota=${dailyQuota}`
    );
  }

  if (dailyQuota === UNLIMITED_DAILY_QUOTA) {
    if (shouldLog) {
      console.log('[isOverDailyQuota] false - Daily quota is unlimited');
    }
    return false;
  }

  const consumedQuotaMinutes = consumedQuotaSeconds / 60;
  const isOverQuota = consumedQuotaMinutes >= dailyQuota;

  if (shouldLog) {
    console.log(
      `[isOverDailyQuota] ${isOverQuota} - consumedQuotaSeconds=${consumedQuotaSeconds}, consumedQuotaMinutes=${consumedQuotaMinutes}, dailyQuota=${dailyQuota}`
    );
  }

  return isOverQuota;
};

export const isDailyQuotaLeftLessOrEqualThan = async (
  deviceUserData: DeviceAndUserStore,
  seconds = 0
) => {
  const timezone = getRulesStore().getTimezone();
  const futureDate = getDateInFuture({ seconds }, await now(), timezone);

  return isOverDailyQuota(
    deviceUserData,
    getDateMs(futureDate),
    getConsumedQuotaSeconds(deviceUserData) + seconds
  );
};
